import React, { useState, useEffect, useCallback } from 'react';
import AdminNav from '../components/AdminNav';
import { getShiftTimes, getHubOversight, saveHubOversight } from '../services/firebase';
import './Admin.css';

const DAYS = ['Thu-April-9', 'Fri-April-10', 'Sat-April-11', 'Sun-April-12'];
const DAY_LABELS = {
  'Thu-April-9':  'Thursday, April 9',
  'Fri-April-10': 'Friday, April 10',
  'Sat-April-11': 'Saturday, April 11',
  'Sun-April-12': 'Sunday, April 12',
};

const BLANK = { schedule: [], shifts: {} };

function HubOversight({ onLogout }) {
  const [selectedDay, setSelectedDay] = useState(DAYS[0]);
  const [shiftTimes, setShiftTimes] = useState([]);
  const [draft, setDraft] = useState(BLANK);       // working copy
  const [saved, setSaved] = useState(BLANK);       // last committed state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const isDirty = JSON.stringify(draft) !== JSON.stringify(saved);

  const loadShiftTimes = useCallback(async () => {
    const times = await getShiftTimes();
    setShiftTimes(times.filter(s => s.active));
  }, []);

  const loadDay = useCallback(async (day) => {
    setLoading(true);
    const doc = await getHubOversight(day);
    const data = doc || { ...BLANK };
    setDraft(data);
    setSaved(data);
    setLoading(false);
  }, []);

  useEffect(() => { loadShiftTimes(); }, [loadShiftTimes]);
  useEffect(() => { loadDay(selectedDay); }, [selectedDay, loadDay]);

  const handleDayChange = async (day) => {
    if (day === selectedDay) return;
    // Auto-save pending changes before switching days
    if (isDirty) {
      setSaving(true);
      await saveHubOversight(selectedDay, draft);
      setSaving(false);
    }
    setSelectedDay(day);
  };

  const handleSave = async () => {
    setSaving(true);
    await saveHubOversight(selectedDay, draft);
    setSaved(draft);
    setSaving(false);
  };

  const handleCancel = () => {
    setDraft(saved);
  };

  const updateSchedule = (idx, field, value) => {
    const schedule = [...(draft.schedule || [])];
    schedule[idx] = { ...schedule[idx], [field]: value };
    setDraft({ ...draft, schedule });
  };

  const addScheduleRow = () => {
    const schedule = [...(draft.schedule || []), { name: '', fromTime: '', toTime: '' }];
    setDraft({ ...draft, schedule });
  };

  const removeScheduleRow = (idx) => {
    const schedule = (draft.schedule || []).filter((_, i) => i !== idx);
    setDraft({ ...draft, schedule });
  };

  const updateShiftAssignment = (shiftTime, field, value) => {
    const shifts = { ...(draft.shifts || {}) };
    shifts[shiftTime] = { ...(shifts[shiftTime] || {}), [field]: value };
    setDraft({ ...draft, shifts });
  };

  if (loading) return <div className="admin-container"><AdminNav onLogout={onLogout} /><div className="loading">Loading…</div></div>;

  return (
    <div className="admin-container">
      <AdminNav onLogout={onLogout} />
      <header className="admin-header">
        <h1>Office / Oversight</h1>
        <p className="page-subtitle">Schedule the on-site oversight team and assign prayer and spiritual points for each shift.</p>
      </header>

      <div className="scheduling-content">
        {/* Day Tabs */}
        <div className="day-selector">
          {DAYS.map(day => (
            <button
              key={day}
              className={`day-tab ${selectedDay === day ? 'active' : ''}`}
              onClick={() => handleDayChange(day)}
            >
              {day.split('-')[0]}
            </button>
          ))}
        </div>

        {/* Save / Cancel bar */}
        {isDirty && (
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', margin: '16px 0', padding: '12px 16px', background: '#fefce8', border: '1px solid #fde047', borderRadius: '8px' }}>
            <span style={{ fontSize: '13px', color: '#92400e', flex: 1 }}>You have unsaved changes.</span>
            <button className="btn btn-primary" style={{ flex: 'none', minWidth: 'auto', padding: '6px 16px', fontSize: '13px' }} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button className="btn btn-secondary" style={{ flex: 'none', minWidth: 'auto', padding: '6px 16px', fontSize: '13px' }} onClick={handleCancel} disabled={saving}>
              Cancel
            </button>
          </div>
        )}

        <h3 style={{ marginTop: isDirty ? '8px' : '24px', marginBottom: '12px', color: '#1e293b' }}>
          {DAY_LABELS[selectedDay]}
        </h3>

        {/* On-Site Team */}
        <div className="location-section" style={{ marginBottom: '24px' }}>
          <div className="shift-header" style={{ padding: '14px 20px', background: '#f8fafc', borderRadius: '8px 8px 0 0', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <strong>On-Site Oversight Team</strong>
            <button
              className="btn btn-primary"
              style={{ flex: 'none', minWidth: 'auto', padding: '4px 12px', fontSize: '12px' }}
              onClick={addScheduleRow}
            >
              + Add Person
            </button>
          </div>
          <div style={{ padding: '16px 20px' }}>
            {(draft.schedule || []).length === 0 && (
              <p style={{ color: '#94a3b8', fontSize: '13px', margin: 0 }}>No one scheduled yet. Click "+ Add Person" to add.</p>
            )}
            {(draft.schedule || []).map((row, idx) => (
              <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 120px 120px auto', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                <input
                  className="search-input"
                  style={{ padding: '7px 10px' }}
                  placeholder="Name"
                  value={row.name || ''}
                  onChange={e => updateSchedule(idx, 'name', e.target.value)}
                />
                <input
                  type="time"
                  className="search-input"
                  style={{ padding: '7px 10px' }}
                  value={row.fromTime || ''}
                  onChange={e => updateSchedule(idx, 'fromTime', e.target.value)}
                  title="From time"
                />
                <input
                  type="time"
                  className="search-input"
                  style={{ padding: '7px 10px' }}
                  value={row.toTime || ''}
                  onChange={e => updateSchedule(idx, 'toTime', e.target.value)}
                  title="To time"
                />
                <button className="btn-delete" onClick={() => removeScheduleRow(idx)} title="Remove">×</button>
              </div>
            ))}
            {(draft.schedule || []).length > 0 && (
              <p style={{ color: '#94a3b8', fontSize: '12px', marginTop: '8px', marginBottom: 0 }}>
                From / To times are optional
              </p>
            )}
          </div>
        </div>

        {/* Shift Assignments */}
        <div className="location-section">
          <div className="shift-header" style={{ padding: '14px 20px', background: '#f8fafc', borderRadius: '8px 8px 0 0' }}>
            <strong>Shift Assignments (Prayer & Spiritual Points)</strong>
          </div>
          <div style={{ padding: '16px 20px' }}>
            {shiftTimes.length === 0 && (
              <p style={{ color: '#94a3b8', fontSize: '13px' }}>No active shift times found. Add them in Settings → Shift Times.</p>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr 1fr', gap: '8px', marginBottom: '8px' }}>
              <div style={{ fontWeight: 600, fontSize: '13px', color: '#64748b' }}>Shift</div>
              <div style={{ fontWeight: 600, fontSize: '13px', color: '#7c3aed' }}>🙏 Prayer</div>
              <div style={{ fontWeight: 600, fontSize: '13px', color: '#c2410c' }}>✨ Spiritual Point</div>
            </div>
            {shiftTimes.map(st => (
              <div key={st.name} style={{ display: 'grid', gridTemplateColumns: '140px 1fr 1fr', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ fontSize: '13px', fontWeight: 500 }}>{st.name}</div>
                <input
                  className="search-input"
                  style={{ padding: '7px 10px' }}
                  placeholder="Person's name"
                  value={draft.shifts?.[st.name]?.prayer || ''}
                  onChange={e => updateShiftAssignment(st.name, 'prayer', e.target.value)}
                />
                <input
                  className="search-input"
                  style={{ padding: '7px 10px' }}
                  placeholder="Person's name"
                  value={draft.shifts?.[st.name]?.spiritualPoint || ''}
                  onChange={e => updateShiftAssignment(st.name, 'spiritualPoint', e.target.value)}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default HubOversight;
